/**
 * <steward-message> — A single message bubble (user or assistant).
 */
import { LitElement, html, css, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { renderMarkdown } from './steward-markdown.js';
import { sharedStyles } from './steward-styles.js';
import { defineOnce } from './register.js';

export interface ArtifactData {
  imageUrl: string;
  downloadUrl: string;
  description: string;
}

/** Resolve [chart:id] placeholders to markdown image links, for text that
 *  leaves the widget: the copy glyph and the conversation export both write it. */
export function resolveChartPlaceholders(
  content: string,
  artifacts: Map<string, ArtifactData> | null,
): string {
  return content.replace(/\[chart:([a-f0-9]+)\]/gi, (_match, id) => {
    const artifact = artifacts?.get(id);
    if (artifact) {
      // Sanitize description to prevent markdown injection
      const safeDesc = artifact.description.replace(/[\[\]()`|~\n\r]/g, '');
      return `![${safeDesc}](${artifact.imageUrl})`;
    }
    return '[chart unavailable]';
  });
}

@defineOnce('steward-message')
export class StewardMessage extends LitElement {
  @property({ type: String }) role: 'user' | 'assistant' | 'error' = 'assistant';
  @property({ type: String }) content = '';
  @property({ type: Boolean }) streaming = false;
  /** Released blocks, in order, each getting its OWN container.
   *
   *  Append-only while a turn reveals. `content` still carries the whole
   *  settled source — copy reads it, the session stores it, and a restored
   *  message renders from it with no blocks at all. Rendering from blocks is
   *  what makes settled text stable STRUCTURALLY: a container for block N is
   *  built once and never touched again, so "earlier blocks hold still" stops
   *  being a property that a rebuild happens to preserve. */
  @property({ attribute: false }) blocks: string[] | null = null;
  /** Blocks remain unrevealed — draws the streaming caret. */
  @property({ type: Boolean }) revealing = false;
  @property({ attribute: false }) artifacts: Map<string, ArtifactData> | null = null;
  /** The text is the model's output: the machine-readable mark, reflected
   *  onto this element as `data-ai-generated`. Set from the message's stored
   *  flag, never from `role`, which server copy shares with model answers. */
  @property({ type: Boolean, reflect: true, attribute: 'data-ai-generated' })
  aiGenerated = false;
  @state() private _copied = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        margin-bottom: 24px;
      }

      .message-wrap {
        position: relative;
        max-width: 85%;
      }

      .message {
        padding: 10px 14px;
        border-radius: var(--steward-border-radius-sm);
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      .copy-btn {
        position: absolute;
        bottom: 4px;
        right: 4px;
        width: 26px;
        height: 26px;
        border: none;
        border-radius: 4px;
        background: none;
        color: var(--steward-text-secondary);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.15s ease;
        padding: 0;
      }

      .message-wrap:hover .copy-btn {
        opacity: 1;
      }

      .copy-btn:hover {
        opacity: 0.7;
      }

      .copy-btn svg {
        width: 14px;
        height: 14px;
      }

      .message-wrap--user {
        margin-left: auto;
      }

      .message-wrap--assistant,
      .message-wrap--error {
        margin-right: auto;
      }

      /* Bubbles use the bubble tier of Liquid Glass — most opaque, least
       * blur, prioritizing prose readability over depth effect. */
      .message--user {
        background: var(--steward-user-bg-glass, var(--steward-user-bg));
        backdrop-filter: blur(var(--steward-bubble-blur, 0));
        -webkit-backdrop-filter: blur(var(--steward-bubble-blur, 0));
        color: var(--steward-user-text);
        border-bottom-right-radius: 4px;
      }

      .message--assistant {
        background: var(--steward-assistant-bg-glass, var(--steward-assistant-bg));
        backdrop-filter: blur(var(--steward-bubble-blur, 0));
        -webkit-backdrop-filter: blur(var(--steward-bubble-blur, 0));
        color: var(--steward-assistant-text);
        border-bottom-left-radius: 4px;
      }

      .message--error {
        background: var(--steward-error-bg-glass, var(--steward-error-bg));
        backdrop-filter: blur(var(--steward-bubble-blur, 0));
        -webkit-backdrop-filter: blur(var(--steward-bubble-blur, 0));
        color: var(--steward-error-text);
        border-bottom-left-radius: 4px;
        font-size: 0.9em;
      }

      /* Markdown content styles */
      .message--assistant p { margin: 0.4em 0; }
      .message--assistant p:first-child { margin-top: 0; }
      .message--assistant p:last-child { margin-bottom: 0; }
      /* Inline <code> and <pre> blocks consume the same theme tokens
       * (--steward-code-bg, --steward-code-text). The tokens flip per
       * theme: light mode resolves to transparent bg + dark text (no
       * chip — code reads as monospace prose on the bubble), dark mode
       * resolves to the dark gray-teal chip + cream text. So the same
       * CSS rule produces "no chip" in light and "dark chip" in dark
       * automatically through the cascade — see steward-theme.ts. */
      .message--assistant code {
        background: var(--steward-code-bg);
        color: var(--steward-code-text);
        font-family: var(--steward-font-mono);
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.9em;
      }
      .message--assistant pre {
        background: var(--steward-code-bg);
        color: var(--steward-code-text);
        font-family: var(--steward-font-mono);
        padding: 10px;
        border-radius: var(--steward-border-radius-sm);
        /* Horizontal only, as it always was. A max-height with a
         * vertical scroll lived here briefly and was REVERSED by the
         * owner 2026-09-08: capping the block changes how a long answer
         * behaves rather than how it looks, and it put a scrollbar
         * inside a chat message. The dark slab that prompted all this
         * was a colour problem, and it was fixed as one. */
        overflow-x: auto;
        /* An edge, because the ground alone no longer draws one. Light
         * themes resolve --steward-code-bg to a quiet wash or to nothing
         * at all, and a code block with neither fill nor border is not a
         * block — it is monospace prose that happens to be indented. */
        border: 1px solid var(--steward-border);
        margin: 0.5em 0;
      }
      .message--assistant pre code {
        background: none;
        color: inherit;
        padding: 0;
      }
      .message--assistant ul, .message--assistant ol {
        padding-left: 1.5em;
        margin: 0.4em 0;
      }
      .message--assistant a {
        color: var(--steward-accent, var(--steward-primary));
        text-decoration: underline;
      }
      .message--assistant strong { font-weight: 600; }
      .message--assistant h2, .message--assistant h3, .message--assistant h4 {
        margin: 0.6em 0 0.3em;
        font-size: 1em;
        font-weight: 600;
      }
      /* A quote's leading edge is what marks it as a quote, so it takes the
       * secondary text ink, which clears the 3:1 floor for a mark a reader
       * must see; the border token does not (owner, 2026-10-14). */
      .message--assistant blockquote {
        margin: 0.4em 0;
        padding-left: 0.75em;
        border-left: 2px solid var(--steward-text-secondary);
      }
      /* A rule only separates, as the space around it already does, so it
       * keeps the quiet border token. */
      .message--assistant hr {
        border: 0;
        border-top: 1px solid var(--steward-border);
        margin: 0.6em 0;
      }

      /* Markdown tables — Option A: flat, scrollable, native.
       * The parser wraps tables in <div class="table-wrap"> so the
       * horizontal scroll lives on the wrapper, not the bubble. */
      .message--assistant .table-wrap {
        overflow-x: auto;
        max-width: 100%;
        margin: 0.5em 0;
        border-radius: var(--steward-border-radius-sm);
      }
      .message--assistant table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.85em;
        table-layout: auto;
      }
      .message--assistant thead th {
        background: var(--steward-bg-secondary);
        font-weight: 600;
        text-align: left;
        padding: 6px 10px;
        border-bottom: 1px solid var(--steward-border);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, var(--steward-glass-edge-light, 0.6));
      }
      .message--assistant tbody td {
        padding: 6px 10px;
        border-top: 1px solid var(--steward-border);
        border-bottom: 1px solid var(--steward-border);
        border-left: none;
        border-right: none;
        text-align: left;
      }
      .message--assistant tbody tr:nth-child(even) td {
        background: color-mix(in srgb, var(--steward-bg-secondary) 50%, transparent);
      }
      .message--assistant .ta-left { text-align: left; }
      .message--assistant .ta-right { text-align: right; }
      .message--assistant .ta-center { text-align: center; }
      .message--assistant table tr:last-child td {
        border-bottom: none;
      }

      /* Card-stacked layout for narrow widget widths.
       * The widget's parent shadow DOM defines a named container
       * "steward" on .panel (container-type: inline-size). Container
       * queries cross shadow boundaries by name, so this block fires
       * when the panel is <= 480px regardless of viewport.
       *
       * Each row becomes a discrete card; each cell stacks its
       * data-label ABOVE the value (iOS Mail/Forms pattern). The
       * earlier side-by-side flex layout squeezed values into a
       * narrow right column, causing one-word-per-line cramping
       * for prose-heavy cells (and turning fixed-width children
       * like code blocks into horizontal-bleed nightmares). */
      @container steward (max-width: 480px) {
        .message--assistant table {
          display: block;
          font-size: 0.8em;
        }
        .message--assistant thead {
          display: none;
        }
        .message--assistant tbody {
          display: block;
        }
        .message--assistant tbody tr {
          display: block;
          border: 1px solid var(--steward-border);
          border-radius: var(--steward-border-radius-sm);
          padding: 8px 10px;
          margin-bottom: 6px;
          background: var(--steward-bg);
        }
        .message--assistant tbody tr:last-child {
          margin-bottom: 0;
        }
        /* Cells are vertical blocks: label-above, value-below. The
         * value gets the full card width so prose wraps naturally
         * and code blocks have room to scroll horizontally inside
         * their own pre wrapper. */
        .message--assistant tbody td {
          display: block;
          padding: 6px 0;
          border: none;
        }
        .message--assistant tbody td:first-child {
          padding-top: 0;
        }
        .message--assistant tbody td:last-child {
          padding-bottom: 0;
        }
        /* Subtle divider between cells within a card. */
        .message--assistant tbody td + td {
          border-top: 1px solid color-mix(in srgb, var(--steward-border) 50%, transparent);
        }
        .message--assistant tbody td::before {
          content: attr(data-label);
          display: block;
          margin-bottom: 2px;
          font-weight: 600;
          color: var(--steward-text-secondary);
          font-size: 0.85em;
        }
        /* Cards are the visual unit now — drop zebra striping. */
        .message--assistant tbody tr:nth-child(even) td {
          background: transparent;
        }
      }

      /* Chart images */
      .message--assistant .steward-chart {
        margin: 8px 0;
        text-align: center;
      }
      .message--assistant .steward-chart img {
        max-width: 100%;
        max-height: 360px;
        border-radius: 6px;
        cursor: pointer;
        object-fit: contain;
        transition: opacity 0.15s ease;
      }
      .message--assistant .steward-chart img:hover {
        opacity: 0.85;
      }
      .message--assistant .steward-chart figcaption {
        font-size: 0.75em;
        color: var(--steward-text-secondary);
        margin-top: 4px;
      }
      .message--assistant .steward-chart .chart-expiry {
        opacity: 0.6;
        font-style: italic;
      }
      .message--assistant .img-expired {
        padding: 12px;
        font-size: 0.85em;
        /* Ink from the SAME pair as the ground. It read
         * --steward-text-secondary over a --steward-code-bg ground, and
         * the two were never a pair: wherever an embedder makes code-bg
         * dark, secondary text lands on it unread — 4.11:1 at 0.85em in
         * the console panel, under AA. A surface takes its ink from the
         * token that ships with its ground. */
        color: var(--steward-code-text);
        background: var(--steward-code-bg);
        border-radius: 6px;
        text-align: center;
      }

      /* Inline citations */
      .message--assistant .citation {
        display: inline-block;
        font-size: 0.75em;
        color: var(--steward-text-secondary);
        font-style: italic;
        opacity: 0.8;
      }

      /* Typing indicator */
      .typing {
        display: inline-flex;
        gap: 4px;
        padding: 4px 0;
      }
      .typing span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--steward-text-secondary);
        animation: typing 1.4s infinite ease-in-out both;
      }
      .typing span:nth-child(1) { animation-delay: 0s; }
      .typing span:nth-child(2) { animation-delay: 0.2s; }
      .typing span:nth-child(3) { animation-delay: 0.4s; }
      @keyframes typing {
        0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
        40% { transform: scale(1); opacity: 1; }
      }

      /* Suppress the looping typing-indicator animation for users who
       * requested reduced motion. The dots remain visible at rest state
       * — feedback that the assistant is working comes from text color +
       * the launcher's reduced-motion-aware streaming style. */
      @media (prefers-reduced-motion: reduce) {
        .typing span {
          animation: none !important;
        }
      }

      /* One released block. The fade lives HERE rather than on the bubble
       * because an animation runs once when its element is created, and
       * these elements are created exactly once — so a settled block cannot
       * re-animate when a later one arrives, by construction rather than by
       * a guard someone has to remember. */
      .msg-block {
        animation: steward-block-in var(--steward-block-fade, 700ms) ease-out both;
      }

      @keyframes steward-block-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      /* The reveal is live: a soft caret at the content edge of the bubble.
       * An ::after on the widget's OWN wrapper, never a node inside the
       * sanitized markdown — ALLOWED_TAGS stays closed and the caret cannot
       * be reached by anything the model emits. */
      .message--assistant.is-revealing::after {
        content: '';
        display: inline-block;
        width: 2px;
        height: 1em;
        margin-left: 2px;
        background: currentColor;
        vertical-align: text-bottom;
        animation: steward-caret 1.1s ease-in-out infinite;
      }

      @keyframes steward-caret {
        0%, 100% { opacity: 0.15; }
        50% { opacity: 0.8; }
      }

      @media (prefers-reduced-motion: reduce) {
        .msg-block { animation: none; }
        .message--assistant.is-revealing::after { animation: none; opacity: 0.5; }
      }

      /* Strip the bubble glass for users who turn off transparency
       * (iOS / macOS Accessibility → Reduce Transparency). */
      @media (prefers-reduced-transparency: reduce) {
        .message--user,
        .message--assistant,
        .message--error {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        .message--user { background: var(--steward-user-bg) !important; }
        .message--assistant { background: var(--steward-assistant-bg) !important; }
        .message--error { background: var(--steward-error-bg) !important; }
      }
    `,
  ];

  private _copyIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>`;
  private _checkIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>`;

  render() {
    const revealing = this.role === 'assistant' && this.revealing;
    const messageClass =
      `message message--${this.role}` + (revealing ? ' is-revealing' : '');
    const wrapClass = `message-wrap message-wrap--${this.role}`;

    if (this.role === 'user') {
      return html`<div class="${wrapClass}"><div class="${messageClass}">${this.content}</div></div>`;
    }

    // Blocks beat the placeholder. The typing dots mean "nothing has arrived
    // yet"; once a block is released something has, even if `content` has not
    // caught up. Ordering these the other way makes the dots outlive the
    // first paint.
    if (this.streaming && !this.content && !(this.blocks && this.blocks.length > 0)) {
      return html`
        <div class="${wrapClass}">
          <div class="message message--assistant">
            <div class="typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      `;
    }

    // Pass streaming flag so the markdown parser can suppress trailing
    // partial GFM table rows (e.g. mid-chunk `| Bob` with no closing pipe
    // / newline). Without this, the table closes early and the partial
    // row flickers as literal text below it until the next chunk arrives.
    /*
     * THE SINK IS CHOSEN PER ROLE, and only the assistant's content is ever
     * put into the DOM as markup.
     *
     * `unsafeHTML` does what its name says. It used to receive `rendered`
     * for EVERY role that got this far, and only the assistant arm had
     * passed through `renderMarkdown`, which is where DOMPurify runs — so
     * an `error` bubble, which is what every refused turn renders as, put
     * its content into the page as HTML with nothing in between. Measured
     * before the fix: the same payload rendered as literal text in a user
     * bubble, was sanitised in an assistant bubble, and in an error bubble
     * created the element and ran its handler.
     *
     * It was latent — the only authors of error content are this file's own
     * literals and the server's `message`, and every typed error inside the
     * stream is caught and replaced with a fixed sentence before it reaches
     * the wire. It stops being latent the moment anyone else can author one,
     * which is exactly what a per-product operator-written refusal string
     * would do. Closing the sink is what makes that safe to build; a
     * validator on the writer would not, because it guards one author and
     * this bubble is open to every other.
     *
     * A non-assistant value stays a VALUE here, interpolated by Lit and
     * therefore escaped — the same route the `user` arm takes by returning
     * early above. Nothing is lost: a refusal is a plain sentence, and the
     * server's own error messages are plain sentences too.
     */
    const rendered =
      this.role === 'assistant'
        ? unsafeHTML(renderMarkdown(this.content, this.streaming))
        : this.content;

    // A released block is COMPLETE, so it renders with streaming=false —
    // streaming mode exists to hide a half-arrived table row and charges for
    // it by requiring that row's trailing newline, which segmentation strips.
    const blocks = this.blocks;
    return html`
      <div class="${wrapClass}">
        <div class="${messageClass}">
          ${blocks !== null && blocks.length > 0
            ? blocks.map(
                (block) =>
                  html`<div class="msg-block">${unsafeHTML(renderMarkdown(block, false))}</div>`,
              )
            : rendered}
        </div>
        ${this.role === 'assistant' && !this.streaming
          ? html`<button class="copy-btn" @click=${this._copyContent} aria-label="Copy to clipboard">${this._copied ? this._checkIcon : this._copyIcon}</button>`
          : ''}
      </div>
    `;
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    // `blocks` joins the gate because per-block append is what now changes
    // the rendered DOM during a reveal. Without it a [chart:id] inside a
    // released block would sit at "Loading chart..." until `streaming`
    // flipped at reveal end — the drip only kept this working by rewriting
    // `content` on every frame.
    if (
      changedProperties.has('content') ||
      changedProperties.has('streaming') ||
      changedProperties.has('artifacts') ||
      changedProperties.has('blocks')
    ) {
      this._resolveArtifactPlaceholders();
    }
  }

  private _resolveArtifactPlaceholders(): void {
    const placeholders = this.shadowRoot?.querySelectorAll('.steward-chart-placeholder[data-artifact-id]');
    if (!placeholders?.length) return;

    placeholders.forEach((el) => {
      const div = el as HTMLElement;
      if (div.dataset.resolved) return;
      const artifactId = div.dataset.artifactId;
      if (!artifactId) return;

      const artifact = this.artifacts?.get(artifactId);
      if (!artifact) {
        if (!this.streaming) {
          // Streaming ended but artifact never arrived
          div.dataset.resolved = '1';
          div.innerHTML = '<div class="img-expired">Chart unavailable</div>';
        } else if (!div.querySelector('.steward-chart-loading')) {
          div.innerHTML = '<div class="steward-chart-loading" style="padding:12px;text-align:center;color:var(--steward-text-secondary);font-size:0.85em;">Loading chart...</div>';
        }
        return;
      }

      div.dataset.resolved = '1';

      // Check if URL is already expired — skip image load entirely
      const expiresAt = StewardMessage._getUrlExpiration(artifact.imageUrl);
      if (expiresAt && expiresAt.getTime() <= Date.now()) {
        const expired = document.createElement('div');
        expired.className = 'img-expired';
        expired.textContent = 'Chart expired - regenerate this response';
        div.replaceWith(expired);
        return;
      }

      const figure = document.createElement('figure');
      figure.className = 'steward-chart';

      const img = document.createElement('img');
      img.src = artifact.imageUrl;
      img.alt = artifact.description;
      img.loading = 'lazy';

      // Click → dispatch preview event (crosses Shadow DOM via composed)
      img.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('image-preview', {
          bubbles: true,
          composed: true,
          detail: { src: img.src, alt: img.alt || 'chart', downloadUrl: artifact.downloadUrl },
        }));
      });

      // Error → show expired placeholder
      img.addEventListener('error', () => {
        const expiredEl = document.createElement('div');
        expiredEl.className = 'img-expired';
        expiredEl.textContent = 'Chart expired - regenerate this response';
        figure.replaceWith(expiredEl);
      }, { once: true });

      const caption = document.createElement('figcaption');
      caption.textContent = artifact.description;

      if (expiresAt) {
        const expiry = document.createElement('span');
        expiry.className = 'chart-expiry';
        expiry.textContent = ` · ${StewardMessage._formatExpiry(expiresAt)}`;
        caption.appendChild(expiry);
      }

      figure.appendChild(img);
      figure.appendChild(caption);
      div.replaceWith(figure);
    });
  }

  /** Extract expiration Date from an S3 presigned URL (V4). */
  private static _getUrlExpiration(url: string): Date | null {
    try {
      const params = new URL(url).searchParams;
      const date = params.get('X-Amz-Date');
      const expires = params.get('X-Amz-Expires');
      if (!date || !expires || date.length < 15) return null;
      const signed = new Date(
        `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}` +
        `T${date.slice(9, 11)}:${date.slice(11, 13)}:${date.slice(13, 15)}Z`,
      );
      if (isNaN(signed.getTime())) return null;
      const expirySec = Number(expires);
      if (isNaN(expirySec) || expirySec <= 0) return null;
      return new Date(signed.getTime() + expirySec * 1000);
    } catch {
      return null;
    }
  }

  /** Format expiration as relative or absolute depending on time remaining. */
  private static _formatExpiry(expiresAt: Date): string {
    const ms = expiresAt.getTime() - Date.now();
    if (ms <= 0) return 'expired';
    const mins = Math.round(ms / 60_000);
    if (mins <= 0) return 'expiring soon';
    if (mins < 60) return `expires in ${mins} min`;
    return `expires at ${expiresAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  }

  private async _copyContent(): Promise<void> {
    try {
      await navigator.clipboard.writeText(resolveChartPlaceholders(this.content, this.artifacts));
      this._copied = true;
      setTimeout(() => { this._copied = false; }, 1500);
    } catch (e) {
      console.warn('[steward-message] Failed to copy to clipboard:', e);
    }
  }
}
